import { ReactNode } from 'react';

import { Box, Divider, Skeleton, Stack, tokens } from '@cast/design-system';

import { InventoryWorkloadNameLink } from './InventoryWorkloadNameLink';
import { WidgetLayout } from './WidgetLayout';
import { InventoryListItem } from './InventoryListItem';

type TopWorkloadsItem = {
  id: string;
  name: string;
  color: string;
  value: ReactNode;
};

export type ChartWithTopWorkloadsCardArgs = {
  testId?: string;
  leftHeader?: ReactNode;
  rightHeader?: ReactNode;
  chart: ReactNode;
  items: TopWorkloadsItem[];
  isListLoading?: boolean;
  listSkeletonCount?: number;
  onItemClick?: (id: string) => void;
  isItemDisabled?: (id: string) => boolean;
  getItemHref?: (item: TopWorkloadsItem) => string | undefined;
};

const MARKER_SIZE = 8;
const DISABLED_OPACITY = 0.25;

type TopWorkloadsListItemProps = {
  item: TopWorkloadsItem;
  onItemClick?: (id: string) => void;
  isItemDisabled?: (id: string) => boolean;
  getItemHref?: (item: TopWorkloadsItem) => string | undefined;
};

const TopWorkloadsListItem = ({
  item,
  onItemClick,
  isItemDisabled,
  getItemHref,
}: TopWorkloadsListItemProps) => {
  const disabled = isItemDisabled?.(item.id) ?? false;
  const href = getItemHref?.(item);

  return (
    <InventoryListItem
      testId={`top-workloads-item-${item.id}`}
      size="sm"
      divider
      onClick={onItemClick ? () => onItemClick(item.id) : undefined}
      sx={{ opacity: disabled ? DISABLED_OPACITY : 1 }}
      slots={{
        startContent: (
          <Box
            data-testid="top-workloads-item-marker"
            sx={{
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              minWidth: MARKER_SIZE,
              minHeight: MARKER_SIZE,
              borderRadius: tokens.theme.radius.xs,
              backgroundColor: item.color,
            }}
          />
        ),
        label: href ? (
          <InventoryWorkloadNameLink to={href}>
            {item.name}
          </InventoryWorkloadNameLink>
        ) : (
          item.name
        ),
        endContent: item.value,
      }}
      slotProps={{
        label: href
          ? undefined
          : {
              variant: 'P12M',
              color: tokens.theme.fg.action.primary,
            },
        endContent: { variant: 'P12B' },
      }}
    />
  );
};

type TopWorkloadsListSkeletonArgs = {
  count: number;
};

const TopWorkloadsListSkeleton = (
  topWorkloadsListSkeletonArgs: TopWorkloadsListSkeletonArgs
) => {
  const { count } = topWorkloadsListSkeletonArgs;
  return (
    <Stack flex={1} overflow="hidden">
      {Array.from({ length: count }).map((_, index) => (
        <Stack
          key={index}
          direction="row"
          alignItems="center"
          gap={6}
          py={6}
          px={4}
        >
          <Skeleton
            variant="circular"
            sx={{
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              minWidth: MARKER_SIZE,
              minHeight: MARKER_SIZE,
            }}
          />
          <Skeleton variant="text" sx={{ flex: 1 }} />
          <Skeleton variant="text" width={40} />
        </Stack>
      ))}
    </Stack>
  );
};

export const ChartWithTopWorkloadsCard = (
  chartWithTopWorkloadsCardArgs: ChartWithTopWorkloadsCardArgs
) => {
  const {
    testId,
    leftHeader,
    rightHeader,
    chart,
    items,
    isListLoading,
    listSkeletonCount = 5,
    onItemClick,
    isItemDisabled,
    getItemHref,
  } = chartWithTopWorkloadsCardArgs;
  return (
    <WidgetLayout testId={testId} padding={false} sx={{ height: '100%' }}>
      <Stack direction="row" height="100%">
        <Stack flex={1} pl={16} pr={16} pb={16} overflow="hidden">
          {leftHeader && (
            <Stack direction="row" alignItems="center" py={12}>
              {leftHeader}
            </Stack>
          )}
          {chart}
        </Stack>
        <Divider orientation="vertical" flexItem />
        <Stack
          data-testid="top-workloads-list-section"
          pl={12}
          pr={16}
          pb={16}
          overflow="hidden"
          sx={{ width: '33.333%', maxWidth: 332 }}
        >
          {rightHeader && (
            <Stack direction="row" alignItems="center" py={12}>
              {rightHeader}
            </Stack>
          )}
          {isListLoading ? (
            <TopWorkloadsListSkeleton count={listSkeletonCount} />
          ) : (
            <Stack flex={1} overflow="auto">
              {items.map((item) => (
                <TopWorkloadsListItem
                  key={item.id}
                  item={item}
                  onItemClick={onItemClick}
                  isItemDisabled={isItemDisabled}
                  getItemHref={getItemHref}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </WidgetLayout>
  );
};
